import { create } from 'zustand';
import { addDays, compareAsc, format, parse, startOfDay, subDays } from 'date-fns';
import { MONTH_FORMAT_DATE, SERVER_FORMAT_DATE } from '@/service/constants/dates';
import { getAvailabilityFullByIds } from '@/service/api/Availability/getAvailabilityFullByIds';
import { TScheduleShort } from '@/domain/Availability.domain';
import { EActivityCategories, ECalendarView } from '@/domain/Activities.domain';
import { TAvailabilityDayFullBody, TAvailabilityDayFullParams } from '@/domain/FullCalendar.domain';
import { TActivityData, TAvailItem, TCalendarFullStore, TList } from './domain';
import { DEFAULT_CALENDAR_FULL_STATE } from './state';
import { getAvailabilityDay } from '@/service/api/FullCalendar/getAvailabilityDay';
import { getAvailabilityDayFull } from '@/service/api/FullCalendar/getAvailabilityDayFull';
import { TDate } from '../Availability/domain';
import { getAvailabilityByMonth } from '@/service/api/Availability/getAvailabilityByMonth';
import { sortByTime } from '@/service/utils/sortByTime';
import { findIcon } from '@/service/utils/findIcon';
import { createWeekDays } from '@/service/creators/createWeekDays';
import { useAvailabilityStore } from '../Availability/useAvailabilityStore';
import { newDateFromString } from '@/service/utils/getDateByTimezone';

const MOCK_CATEGORIES = [
    EActivityCategories.BOAT,
    EActivityCategories.DOLPHIN,
    EActivityCategories.PARASAILING,
    EActivityCategories.PONTOON,
    EActivityCategories.SUNSET,
    EActivityCategories.TOURIST,
];

export const useCalendarFullStore = create<TCalendarFullStore>((set, get) => ({
    ...DEFAULT_CALENDAR_FULL_STATE,
    getSchedules: async (ids: string, fromDate: Date) => {
        set({ loading: { ...get().loading, schedules: true } });

        try {
            const arrIds = ids.split(',');

            const from = format(fromDate, SERVER_FORMAT_DATE);
            const to = format(addDays(fromDate, 6), SERVER_FORMAT_DATE);

            const avails = await Promise.all(
                arrIds.map((id) => getAvailabilityFullByIds({ id, from, to }))
            );

            const newSchedules = avails.reduce(
                (result, trip, index) => {
                    trip.days_info.forEach((day, i) => {
                        if (!result[day.date]) {
                            result[day.date] = [];
                        }

                        result[day.date] = [
                            ...result[day.date],
                            ...day.rates[0].schedules.map(({ time, id }) => ({
                                date: day.date,
                                time,
                                trip_id: day.product_id,
                                id: id.toString(),
                                name: day.product_name,
                                category: MOCK_CATEGORIES[i >= MOCK_CATEGORIES.length ? 0 : i],
                                isAvail: day.rates[0].is_avail,
                                ticketsAvail: day.rates[0].tickets_avail,
                            })),
                        ];
                    });

                    if (index + 1 === avails.length) {
                        for (const key in result) {
                            result[key] = result[key].sort((a, b) =>
                                compareAsc(
                                    parse(a.time, 'h:mma', new Date()),
                                    parse(b.time, 'h:mma', new Date())
                                )
                            );
                        }
                    }

                    return result;
                },
                {} as { [key: string]: TScheduleShort[] }
            );

            set({ schedules: newSchedules });
        } catch (err) {
            console.error('Error on fetching schedules', err);
            set({ schedules: get().schedules });
        } finally {
            set({ loading: { ...get().loading, schedules: false } });
        }
    },
    setTripSettingsAreFilled: (tripSettingsAreFilled) => set({ tripSettingsAreFilled }),
    getTripSchedules: async (
        scheduleId: string,
        params: TAvailabilityDayFullParams,
        data: TAvailabilityDayFullBody
    ) => {
        set({ loading: { ...get().loading, availabilityDayFull: true } });

        try {
            const tripSchedules = await getAvailabilityDayFull(params, data);

            const tripSingleSchedule = {
                ...tripSchedules,
                additional_options: null,
                schedules: null,
                schedule: Object.values(tripSchedules.schedules).find(
                    (s) => s.id.toString() === scheduleId
                ),
            };

            set({ tripSchedules, tripSingleSchedule });
        } catch (err) {
            console.error('Error on fetching tripSchedules', err);
            set({
                tripSchedules: get().tripSchedules,
                tripSingleSchedule: get().tripSingleSchedule,
            });
        } finally {
            set({ loading: { ...get().loading, availabilityDayFull: false } });
        }
    },
    getMonthsAvail: async (newDate: TDate) => {
        set({
            isActivitiesLoading: true,
        });

        const {
            ids,
            partnerSlug,
            date,
            activities,
            icons,
            prices,
            agreements,
            calendarStatuses,
            isUseSchedules,
            isUseCalendar,
        } = get();
        const arrIds = ids.split(',');

        const newFrom =
            (newDate && newDateFromString(newDate)) ||
            (date.from && newDateFromString(date.from)) ||
            new Date();
        const newTo = addDays(newFrom, 6);

        const months = [
            format(newFrom, MONTH_FORMAT_DATE),
            ...(newFrom.getMonth() !== newTo.getMonth() ? [format(newTo, MONTH_FORMAT_DATE)] : []),
        ];

        const list = arrIds.map((id) => months.map((month) => ({ id, month }))).flat();

        const newMonthAvails = await Promise.all(
            list.map(({ id, month }) => getAvailabilityByMonth(partnerSlug, id, month))
        );

        const newMonthData = list.reduce((res, item, index) => {
            const key = `${item.id}_${newDateFromString(item.month).getMonth()}`;
            res[key] = newMonthAvails[index];

            return res;
        }, {} as TList);

        const newShowList = Array(7)
            .fill(0)
            .map((_, index) => {
                const today = index ? addDays(newFrom, index) : newFrom;
                const day = today.getDate().toString();
                const month = today.getMonth();

                const dayList = activities.filter(
                    ({ id }) => !!newMonthData[`${id}_${month}`]?.includes(day)
                );

                return dayList.map(({ name, id }) => ({
                    name,
                    id,
                    slug: `/${partnerSlug}/details/${id}/`,
                    icon: icons[id],
                    agreement: agreements[id],
                    calendarStatus: calendarStatuses[id],
                    isUseSchedule: isUseSchedules[id],
                    isUseCalendar: isUseCalendar[id],
                    loading: false,
                    price: prices[id],
                }));
            });

        set((state) => ({
            isActivitiesLoading: false,
            monthAvails: {
                ...state.monthAvails,
                ...newMonthData,
            },
            listToShow: newShowList,
        }));
    },

    getDaysAvail: async (newDate: TDate) => {
        set({
            isTimeLoading: true,
            listToShow: Array(7).fill([
                {
                    name: '',
                    id: '',
                    slug: '',
                    icon: '',
                    loading: true,
                },
            ]),
        });

        const {
            ids,
            partnerSlug,
            date,
            icons,
            prices,
            agreements,
            calendarStatuses,
            isUseSchedules,
            isUseCalendar,
        } = get();
        const arrIds = ids.split(',');
        const newFrom =
            (newDate && newDateFromString(newDate)) ||
            (date.from && newDateFromString(date.from)) ||
            new Date();

        const days = Array(7)
            .fill(0)
            .map((_, index) => (index ? addDays(newFrom, index) : newFrom));

        for (let index = 0; index < days.length; index++) {
            const day = days[index];
            const currentFrom = get().date.from;
            const dateString = format(day, SERVER_FORMAT_DATE);

            if (
                currentFrom &&
                startOfDay(currentFrom).getTime() === startOfDay(newFrom).getTime()
            ) {
                const avails = await Promise.all(
                    arrIds.map((id) =>
                        getAvailabilityDay(
                            { partnerId: partnerSlug },
                            { trip_id: id, date: dateString }
                        )
                    )
                );

                const list = avails
                    .flat()
                    .filter((avail) => !!avail?.trip_id)
                    .map(({ trip_id, trip_name, schedules }) => {
                        return schedules.map(({ time, schedule_id, is_avail, tickets }) => ({
                            name: trip_name,
                            id: trip_id,
                            slug: `/${partnerSlug}/details/${trip_id}/`,
                            icon: icons[trip_id],
                            agreement: agreements[trip_id],
                            calendarStatus: calendarStatuses[trip_id],
                            isUseSchedule: isUseSchedules[trip_id],
                            isUseCalendar: isUseCalendar[trip_id],
                            loading: false,
                            time,
                            schedule_id,
                            is_avail,
                            tickets,
                            price: prices[trip_id],
                        }));
                    })
                    .flat()
                    .filter(({ is_avail, tickets }) => is_avail || tickets.avail);

                const newAvails = sortByTime(list) as TAvailItem[];

                const currFrom = get().date.from;
                if (currFrom && startOfDay(currFrom).getTime() === startOfDay(newFrom).getTime()) {
                    set((state) => {
                        const newShowList = [...state.listToShow];
                        newShowList[index] = newAvails;

                        return {
                            isTimeLoading: !(index + 1 === days.length),
                            daysAvails: {
                                ...state.daysAvails,
                                [dateString]: newAvails,
                            },
                            listToShow: newShowList,
                        };
                    });
                }
            }
        }
    },

    initAvailability: (activities, ids, partnerSlug, date) => {
        const fromDate = date ? newDateFromString(date) : new Date();
        const viewBy = get().viewBy;

        const from = fromDate;
        const to = addDays(fromDate, 6);
        const week = createWeekDays(from);
        const { icons, prices, agreements, calendarStatuses, isUseSchedules, isUseCalendar } =
            activities.reduce(
                (res, { name, id, min_price, agreement, statuses, use_schedule, use_calendar }) => {
                    const icon = findIcon(name);
                    res.icons[id] = icon;
                    res.prices[id] = min_price;
                    res.agreements[id] = agreement;
                    res.calendarStatuses[id] = statuses;
                    res.isUseSchedules[id] = use_schedule;
                    res.isUseCalendar[id] = use_calendar;

                    return res;
                },
                {
                    icons: {},
                    prices: {},
                    agreements: {},
                    calendarStatuses: {},
                    isUseSchedules: {},
                    isUseCalendar: {},
                } as TActivityData
            );

        set({
            activities,
            ids,
            partnerSlug,
            date: {
                from,
                to,
                week,
            },
            isActivitiesLoading: false,
            isTimeLoading: false,
            listToShow: [],
            icons,
            prices,
            agreements,
            calendarStatuses,
            isUseSchedules,
            isUseCalendar,
        });

        if (viewBy === ECalendarView.ACTIVITIES) {
            get().getMonthsAvail(from);
        }

        if (viewBy === ECalendarView.TIME) {
            get().getDaysAvail(from);
        }
    },
    changeViewMode: (mode) => {
        set({
            viewBy: mode,
        });

        const { date, getMonthsAvail, getDaysAvail } = get();

        if (mode === ECalendarView.ACTIVITIES) {
            getMonthsAvail(date.from);
        }

        if (mode === ECalendarView.TIME) {
            getDaysAvail(date.from);
        }
    },
    updateDate: (date) => {
        const newDate = date < new Date() ? new Date() : date;

        const { activities, ids, partnerSlug, initAvailability } = get();

        initAvailability(activities, ids, partnerSlug, format(newDate, SERVER_FORMAT_DATE));
    },
    nextWeek: () => {
        const currentDate = get().date.from as Date;
        const nextDate = addDays(currentDate, 7);
        get().updateDate(nextDate);
    },
    prevWeek: () => {
        const currentDate = get().date.from as Date;
        const prevDate = subDays(currentDate, 7);
        get().updateDate(prevDate);
    },
    showSchedule: (tripId, date) => {
        const { initDate, getDay } = useAvailabilityStore.getState();

        initDate(date, tripId);
        getDay(date);
    },
    showTickets: (tripId, scheduleId, date) => {
        const { initDate, selectSchedule } = useAvailabilityStore.getState();

        initDate(date, tripId);
        selectSchedule(+scheduleId);
    },
}));
